import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../utilities/api';
import { createPasskeyAttestation, isPasskeySupported } from '../utilities/webauthn';

const PASSKEYS_ENABLED = import.meta.env.VITE_ENABLE_PASSKEYS !== 'false';

function PasskeySettings() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState('');

  const loadPasskeys = async () => {
    if (!PASSKEYS_ENABLED) return;
    setLoading(true);
    const result = await api.listPasskeys();
    if (result?.success) {
      setPasskeys(result.passkeys || []);
    } else if (result?.error) {
      toast.error(result.error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPasskeys();
  }, []);

  const handleEnroll = async () => {
    if (!isPasskeySupported()) {
      toast.error('Passkeys are not supported on this browser.');
      return;
    }

    setLoading(true);
    try {
      const options = await api.getPasskeyRegisterOptions({
        origin: window.location.origin,
      });
      if (options?.error) throw new Error(options.error);

      const attestation = await createPasskeyAttestation(options);
      const result = await api.verifyPasskeyRegister({
        challengeId: options.challengeId,
        challenge: options.publicKey?.challenge || options.challenge,
        attestation,
        device_label: deviceLabel.trim() || undefined,
        origin: window.location.origin,
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to enroll passkey.');
      }

      setPasskeys(result.passkeys || []);
      setDeviceLabel('');
      toast.success('Passkey added.');
    } catch (err) {
      toast.error(err?.message || 'Passkey enrollment failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const result = await api.deletePasskey({ id });
    if (result?.success) {
      setPasskeys(result.passkeys || []);
      toast.success('Passkey removed.');
    } else {
      toast.error(result?.error || 'Failed to remove passkey.');
    }
    setLoading(false);
  };

  if (!PASSKEYS_ENABLED) return null;

  return (
    <div
      className="mt-6 rounded-md border border-neutral p-4"
      onInput={(event) => event.stopPropagation()}
      onChange={(event) => event.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-primary">Passkeys</h3>
      <p className="mt-1 text-sm text-neutral-dark">
        Use a device passkey for faster sign-in.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label className="sr-only" htmlFor="passkey-device-label">Device label</label>
        <input
          id="passkey-device-label"
          type="text"
          value={deviceLabel}
          onChange={(event) => setDeviceLabel(event.target.value)}
          className="flex-1 rounded border border-neutral px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Device label"
          disabled={loading}
        />
        <button
          type="button"
          onClick={handleEnroll}
          disabled={loading || !isPasskeySupported()}
          className="rounded-md bg-button px-4 py-2 text-sm font-medium text-white hover:bg-button-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
        >
          Add passkey
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {passkeys.length === 0 && (
          <p className="text-sm text-neutral-dark">No passkeys enrolled.</p>
        )}
        {passkeys.map((passkey) => (
          <div key={passkey.id} className="flex items-center justify-between gap-3 rounded border border-neutral-light px-3 py-2">
            <div>
              <p className="text-sm font-medium text-neutral-darkest">{passkey.device_label || 'Passkey'}</p>
              <p className="text-xs text-neutral-dark">
                Added {passkey.created_at || 'recently'}
                {passkey.last_used_at ? ` - Last used ${passkey.last_used_at}` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleDelete(passkey.id)}
              disabled={loading}
              className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PasskeySettings;
